import { useParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import Breadcrumb from "../../../shared/components/layout/Breadcrumb";
import PageLayout from "../../../shared/components/layout/PageLayout";
import PageTitle from "../../../shared/components/layout/PageTitle";
import Wrapper from "../../../shared/components/layout/Wrapper";
import SEO from "../../../shared/components/layout/SEO";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import ResponsiveImage from "../../../shared/components/elements/ResponsiveImage";

import CourseDifficulty from "../components/CourseDifficulty";
import CourseInfoPanel from "../components/CourseInfoPanel";
import CourseSyllabus from "../components/CourseSyllabus";
import { useCourse } from "../hooks/useCourse";
import { useEnrollment } from "../hooks/useEnrollment";
import { COURSE_MESSAGES } from "../messages";
import DOMPurify from "dompurify";

const CourseDetailPage = () => {
    const { course_id } = useParams<{ course_id: string }>();
    const { lang, t } = useLanguage();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const { course, loading, error } = useCourse(course_id);
    const { enroll, enrolling, enrollError } = useEnrollment(course_id);

    // Hooks are all above: early returns below are safe
    if (loading) {
        return (
            <PageLayout>
                <Wrapper>
                    <StatusMessage>{COURSE_MESSAGES.loadingCourse}</StatusMessage>
                </Wrapper>
            </PageLayout>
        );
    }

    if (error || !course) {
        return (
            <PageLayout>
                <Wrapper>
                    <StatusMessage>
                        {error ? COURSE_MESSAGES.courseFailed : COURSE_MESSAGES.courseNotFound}
                    </StatusMessage>
                </Wrapper>
            </PageLayout>
        );
    }

    const breadcrumbItems = [
        { link: ROUTES.courses(lang), title: COURSE_MESSAGES.courses },
        { link: ROUTES.course(lang, course.id), title: course.title }
    ];

    return (
        <>
            <SEO
                title={`${course.title} | ${t.nav.brand}`}
                description={course.description || COURSE_MESSAGES.defaultDescription(course.title)}
                path={`/course/${course.id}`}
            />

            <PageLayout>
                <Breadcrumb items={breadcrumbItems} />

                <Wrapper>
                    <PageTitle title={course.title} />

                    <div className="flex flex-1 flex-col lg:flex-row w-full mt-2 gap-5">
                        {/* Main content */}
                        <div className="w-full lg:w-1/2 flex flex-col gap-5">
                            {course.image && (
                                <ResponsiveImage
                                    className="rounded-md"
                                    src={course.image}
                                    alt={course.title}
                                />
                            )}

                            <CourseDifficulty difficulty={course.difficulty} />

                            <h2 className="mt-3 mb-2 text-2xl font-bold">
                                {COURSE_MESSAGES.description}
                            </h2>
                            <p className="text-lg text-gray-800 leading-9 whitespace-pre-line">
                                {course.description}
                            </p>

                            <h2 className="mt-8 mb-3 text-2xl font-bold">
                                {COURSE_MESSAGES.syllabus}
                            </h2>
                            <CourseSyllabus items={course.syllabus} />
                        </div>

                        <CourseInfoPanel
                            course={course}
                            isAuthenticated={isAuthenticated}
                            authLoading={authLoading}
                            enrolling={enrolling}
                            enrollError={enrollError}
                            onEnroll={enroll}
                        />
                    </div>

                    <div
                        id="course-text"
                        dir="rtl"
                        className="course-content blog w-full mt-10"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(course.course_text || ""),
                        }}
                    />
                </Wrapper>
            </PageLayout>
        </>
    );
};

export default CourseDetailPage;